import React, { useEffect, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import PropTypes from 'prop-types';
import TextareaAutosize from 'react-textarea-autosize';

import useFileComments from './useFileComments';
import { getAsset } from '../../../util/assets';
import Spinner from '../Spinner';
import RootFileCommentItem from './components/RootFileCommentItem';
import FileGroupedReactions from './components/FileGroupedReactions';
import Button from '../Button';
import SendIcon from '../../icons/SendIcon';
import Label from '../Label';

const propTypes = {
  photoId: PropTypes.string,
  videoId: PropTypes.string,
  onGroupedReactionsClick: PropTypes.func,
  target: PropTypes.shape({
    fileCommentId: PropTypes.string,
    parentFileCommentId: PropTypes.string,
  }),
  privateCommentingEnabled: PropTypes.bool,
  forceRepliesOpen: PropTypes.bool,
};

const FileComments = ({
  photoId,
  videoId,
  onGroupedReactionsClick,
  target,
  privateCommentingEnabled = true,
  forceRepliesOpen = false,
}) => {
  const {
    loading,
    comments,
    commentsTotal,
    onCommentCreate,
    onCommentUpdate,
    onCommentDelete,
    user,
    isOwner,
    isUploader,
    entity,
    onLoadMore,
    loadingMore,
  } = useFileComments({
    photoId,
    videoId,
  });
  const [commentCreated, setCommentCreated] = useState(false);
  const [comment, setComment] = useState('');
  const [targetFound, setTargetFound] = useState(!target);
  const fileCommentListRef = useBottomScrollListener(() => {
    if (commentsTotal > comments.length && !loadingMore) {
      onLoadMore();
    }
  }, 200);

  useEffect(() => {
    if (commentCreated) {
      setCommentCreated(false);
      fileCommentListRef.current?.scrollTo?.(
        0,
        fileCommentListRef.current?.scrollHeight,
      );
    }
  }, [commentCreated]);

  useEffect(() => {
    // Focus on the parent first
    const targetFileCommentElement = document.getElementById(
      `comment-${target?.fileCommentId}`,
    );

    if (!loading && !loadingMore && targetFileCommentElement && !targetFound) {
      // Avoids doing this on subsequent re-renders and the like.
      setTargetFound(true);
      targetFileCommentElement.scrollIntoView();
    }

    if (
      !loadingMore &&
      commentsTotal > comments.length &&
      !targetFileCommentElement
    ) {
      onLoadMore();
    }
  }, [loading, loadingMore, target?.fileCommentId, targetFound]);

  if (loading) {
    return (
      <div className="file-comments-wrapper">
        <div className="d-flex justify-content-center align-items-center mt-2">
          <Spinner className="m-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="file-comments-wrapper">
      {entity?.grouped_reactions.total > 0 && (
        <FileGroupedReactions
          groupedReactions={entity?.grouped_reactions.records ?? []}
          onGroupedReactionsClick={onGroupedReactionsClick}
          isUploader={isUploader}
        />
      )}
      {commentsTotal > 0 ? (
        <div className="file-comments-list" ref={fileCommentListRef}>
          {comments.map(accountFileComment => (
            <RootFileCommentItem
              accountFileComment={accountFileComment}
              onCommentUpdate={onCommentUpdate}
              onCommentDelete={onCommentDelete}
              key={accountFileComment.id}
              user={user}
              isOwner={isOwner}
              forceRepliesOpen={
                target?.parentFileCommentId === accountFileComment?.id ||
                forceRepliesOpen
              }
              target={target}
            />
          ))}
          {loadingMore && (
            <div className="w-100 my-2 d-flex">
              <Spinner className="m-auto" />
            </div>
          )}
        </div>
      ) : (
        <div className="file-comments-null-state">
          <img
            src={getAsset('/waldo-icon.svg')}
            className="grayscale-faded mb-2"
            width="40"
            alt="Waldo Icon"
          />
          <p>Loving this {photoId ? 'photo' : 'video'}? Add a comment!</p>
        </div>
      )}
      {privateCommentingEnabled && (
        <div className="file-comments-notice">
          <p>
            Your comments are only visible to the event owner, the uploader, and
            yourself.
          </p>
        </div>
      )}
      <div className="pos-rel">
        <Label labelSrOnly label="Add new comment" id="new-comment" />
        <TextareaAutosize
          id="new-comment"
          className="new-comment-input"
          placeholder="Add a comment..."
          name="newComment"
          value={comment}
          onChange={event => {
            setComment(event.target.value);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.stopPropagation();
              event.preventDefault();
              onCommentCreate(comment);
              setComment('');
              setCommentCreated(true);
            }
          }}
          maxRows={10}
          minRows={3}
        />
        <Button
          data-testid="new-comment-send-button"
          className="new-comment-send-button"
          btnStyle="reset"
          disabled={comment.length === 0}
          onClick={() => {
            onCommentCreate(comment);
            setComment('');
            setCommentCreated(true);
          }}
        >
          <SendIcon color={comment ? '#0d84fe' : '#cdd6e4'} />
        </Button>
      </div>
    </div>
  );
};

FileComments.propTypes = propTypes;

export default FileComments;
